// What the operator session may do, as Power grants it (FNSTPWR-116): the `scopes` the operator
// session reports, which Power IAM derives from the member's role (`roles.ts`), `*` granting
// everything. The screens offer an action only where its permission is granted; Power refuses
// the rest with 403 anyway, so a session not read yet, or unreadable, is offered nothing.
import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { Result } from 'effect';
import { sessionScopesQueryOptions } from '../opin-applications/client/edge.ts';
import { DEVELOPER_PATHS } from '../ui/navigation.ts';

/**
 * The permissions the Admin reads: reading the deployment (every role but a developer), acting on
 * it, approving, managing users, and reading the API documentation (every role).
 */
export const SESSION_PERMISSIONS = [
  'common.read',
  'common.write',
  'regulatory.approve',
  'iam.manage',
  'docs.read',
] as const;
export type SessionPermission = (typeof SESSION_PERMISSIONS)[number];

/** Whether the session may take an action: not read yet, unreadable, refused or granted. */
export const PERMISSION_STATES = ['unread', 'unreadable', 'denied', 'allowed'] as const;
export type PermissionState = (typeof PERMISSION_STATES)[number];

const ALL_SCOPES = '*';

/** Whether a session reporting `scopes` holds `permission`: `*` grants everything. */
export const scopesGrant = (scopes: readonly string[], permission: SessionPermission): boolean =>
  scopes.includes(ALL_SCOPES) || scopes.includes(permission);

/**
 * Whether the root sends the session at `pathname` to Primeiros passos: a session whose scopes lack
 * `common.read` (a developer) reads the Developer screens needing `docs.read` alone. Scopes that
 * cannot be read send nobody anywhere; Power refuses what the role may not read anyway.
 */
export const developerOnlyRedirect = async (
  queryClient: QueryClient,
  pathname: string,
): Promise<boolean> => {
  const scopes = await queryClient.query(sessionScopesQueryOptions);
  return (
    Result.isSuccess(scopes) &&
    !scopesGrant(scopes.success, 'common.read') &&
    !DEVELOPER_PATHS.some((path) => pathname.startsWith(path))
  );
};

/** Whether the session may take an action needing `permission`. */
export const usePermission = (permission: SessionPermission): PermissionState => {
  const query = useQuery(sessionScopesQueryOptions);
  if (query.status === 'pending') return 'unread';
  if (query.status === 'error' || Result.isFailure(query.data)) return 'unreadable';
  const scopes = query.data.success;
  return scopesGrant(scopes, permission) ? 'allowed' : 'denied';
};

/** The hint a screen shows beside an action the session's role may not take. */
export const ROLE_REFUSAL_HINT = 'Seu papel não permite esta ação.';

/** What a screen says when Power refuses the session as expired (401). */
export const SESSION_EXPIRED_TEXT = 'A sessão expirou. Entre de novo para continuar.';
